import { StruggleCompetency, StruggleData } from '@/models/groups/report';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import DountChart from '@/components/charts/dount-chart/dount-chart';
import StrugglingCompetencyCard from '../struggling-competency-card/struggling-competency-card';
import StrugglingDomainCard from '../struggling-domain-card/struggling-domain-card';
import StrugglingDomainFilterCard from '../struggling-domain-filter-card/struggling-domain-filter-card';
import { forEach } from 'jszip';

@Component({
    name: 'struggling-panel-card',
    components: {
        'material-icon': GoogleMaterialIcon,
        'dount-chart': DountChart,
        'struggling-competency-card': StrugglingCompetencyCard,
        'struggling-domain-card': StrugglingDomainCard,
        'struggling-domain-level-filter-card': StrugglingDomainFilterCard,
    },
})

export default class StrugglingPanelCard extends Vue {

    private get gradeCounts() {
        return this.groupType !== 'class'
            ? this.learningReportData.length
            : this.strugglingCompetencies.length;
    }

    private get competenciesCount() {
        return this.groupType !== 'class'
            ? this.learningReportData.reduce((a: any, b: any) => a + b.competencyCount, 0)
            : this.strugglingCompetencies.reduce((a: any, b: any) => a + b.competencies.length, 0);
    }

    private get chartData() {
        return this.getChartData();
    }

    // ---------------------------------------------------------
    // Properties

    @Prop()
    private strugglingCompetencies!: StruggleData[];

    @Prop()
    private learningReportData: any;

    @Prop()
    private groupType!: string;

    @Prop()
    private activeSubject: any;
    @Prop()
    private isLoading!: boolean;

    private domainItems: any = [];

    private filtersList: any = [];

    private filtersListData: any = {
        subjects: {
            keyName: 'subject',
            items: [],
            matchKey: 'subjectCode',
            selected: [],
        },
        grades: {
            keyName: 'grade',
            matchKey: 'gradeId',
            items: [],
            selected: [],
        },
        domains: {
            keyName: 'domainName',
            matchKey: 'domainCode',
            items: [],
            selected: [],
        },
        topics: {
            keyName: 'topicName',
            matchKey: 'topicCode',
            items: [],
            selected: [],
        },
        competencies: {
            keyName: 'displayCode',
            matchKey: 'compCode',
            items: [],
            selected: [],
        },
    };

    private filteredContent: any = [];

    public onSelectAll(dataList: any, isChecked: any) {
        this.$set(dataList, 'selected', isChecked ? [...dataList.items] : []);
        this.parseStruggleFilter();
    }

    // ----------------------------------------------------------------------------
    // Hooks

    private created() {
        this.filterActiveSubject();
    }

    private mounted() {
      // Adding aria-busy attribute to avoid accessibility issues
      setTimeout(() => {
        const element: any = document.getElementById('strugglingPanelCardId');
        if (element) {
          const ulTag: any = element.querySelectorAll('.grade-filter-dropdown ul');
          if (ulTag && ulTag.length > 0) {
            ulTag.forEach((tag: any) => {
              tag.setAttribute('aria-busy', 'true');
            });
          }
        }
      }, 500);
    }

    // ----------------------------------------------------------------------------
    // Actions

    private onCheckItem(item: any, filterObj: any, index: number) {
        const selectedItem = filterObj.selected || [];
        const itemIndex = selectedItem.findIndex((itemData: any) =>
            itemData[filterObj.matchKey] === item[filterObj.matchKey]);
        if (itemIndex !== -1) {
            selectedItem.splice(itemIndex, 1);
        } else {
            selectedItem.push(item);
        }
        this.parseStruggleFilter();
    }
    // ----------------------------------------------------------------------------
    // Methods

    private getChartData() {
        const learningReportData = this.groupType !== 'class'
            ? this.learningReportData : this.strugglingCompetencies;
        const chartData: any = [];
        const sequenceColor: any = d3.scaleLinear().domain([1, learningReportData.length])
            .range(['#00ace9' as any, '#c1dbee' as any]);
        learningReportData.forEach((grade: any, index: number) => {
            chartData.push({
                name: grade.grade,
                value: grade.competencyCount || grade.competencies.length,
                color: sequenceColor(index),
            });
        });
        return chartData;
    }

    private iconLoader(competencies: StruggleCompetency[], count: any) {
        const userCount = count <= 7 ? count : 7;
        return userCount ? new Array(userCount).fill({}) : [];
    }

    private parseStruggleFilter() {
        let learningReportData = JSON.parse(JSON.stringify(this.learningReportData));
        const filtersList = this.filtersList;
        const filterContent = (item: any, filterKey: string) => {
            const { selected, matchKey } = filtersList[filterKey] || {};
            return selected.length ? selected.some((selectedSub: any) =>
                selectedSub[matchKey] === item[matchKey]) : true;
        };

        learningReportData = learningReportData.filter((gradeItem: any) => {

            gradeItem.subjects = [...gradeItem.subjects].filter((subjectItem: any) => {
                subjectItem.domains = ([...subjectItem.domains] || []).filter((domainItem: any) => {
                    domainItem.topics = ([...domainItem.topics] || []).filter((topicItem: any) => {
                        topicItem.competencies = ([...topicItem.competencies] || []).filter((competency: any) => {
                            return filterContent(competency, 'competencies');
                        });
                        topicItem.competencyCount = topicItem.competencies.length;
                        return topicItem.competencies.length && filterContent(topicItem, 'topics');
                    });
                    domainItem.competencyCount = domainItem.topics.reduce((a: any, b: any) => a + b.competencyCount, 0);
                    return domainItem.topics.length && filterContent(domainItem, 'domains');
                });
                subjectItem.competencyCount = subjectItem.domains.reduce((a: any, b: any) => a + b.competencyCount, 0);
                return subjectItem.domains.length && (!!this.activeSubject || filterContent(subjectItem, 'subjects'));
            });
            gradeItem.competencyCount = gradeItem.subjects.reduce((a: any, b: any) => a + b.competencyCount, 0);
            return gradeItem.subjects.length && filterContent(gradeItem, 'grades');

        });
        this.filteredContent = learningReportData;
    }

    @Watch('activeSubject')
    private filterActiveSubject() {
        this.parseDropdownListItem();
    }

    private parseDropdownListItem() {
        const learningReportData = [...this.learningReportData];
        const filtersListData = Object.assign({}, this.filtersListData);

        const { grades, subjects, domains, topics, competencies } = filtersListData;
        learningReportData.forEach((grade: any) => {
            this.updateItems(grades, grade);
            if (grade.subjects) {
                grade.subjects.forEach((subject: any) => {
                    if (!this.activeSubject) { this.updateItems(subjects, subject); }

                    if (subject.domains) {
                        subject.domains.forEach((domain: any) => {
                            this.updateItems(domains, domain);
                            if (domain.topics) {
                                domain.topics.forEach((topic: any) => {
                                    this.updateItems(topics, topic);
                                    if (topic.competencies) {
                                        const competencyList  = competencies.items.concat(topic.competencies);
                                        competencies.items = competencyList;
                                        competencies.selected = [...competencyList];
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });

        if (this.activeSubject) {
            delete filtersListData.subjects;
        }
        this.filtersList = filtersListData;
        this.filteredContent = [...this.learningReportData];
    }

    private updateItems(dropdownList: any, dropdownItem: any) {
        const { items, matchKey, selected } = dropdownList;
        const hasMatch = items.find((item: any) => item[matchKey] === dropdownItem[matchKey]);
        if (!hasMatch) {
            items.push(dropdownItem);
        }
        dropdownList.selected = [...items];
    }

}
